import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

const featuredComics = comics.filter(c => c.featured);
const latestComics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
const popularComics = [...comics].sort((a, b) => b.views - a.views);

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = '基德漫画网 - 好看韩漫免费漫画在线观看下拉式';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '基德漫画网提供好看韩漫漫画免费漫画，支持韩漫免费漫画在线观看下拉式，阅读更便捷。海量韩漫资源，每日更新，高清画质免费阅读。');
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', '好看韩漫,漫画免费漫画,韩漫免费漫画,在线观看下拉式,基德漫画网');
  }, [location]);

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: '基德漫画网',
              url: 'https://www.jidemanhua.com',
              description: '基德漫画网提供好看韩漫漫画免费漫画，支持韩漫免费漫画在线观看下拉式，阅读更便捷。',
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.jidemanhua.com/search?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: '基德漫画网',
              url: 'https://www.jidemanhua.com',
              description: '国内领先的韩漫在线阅读平台，提供海量高清韩漫免费阅读。',
            }),
          }}
        />

        <section className="relative h-[520px] md:h-[620px] flex items-center">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20abstract%20colorful%20illustration%20with%20warm%20orange%20and%20dark%20tones%20dramatic%20atmosphere%20manga%20panel%20aesthetic%20modern%20minimal%20composition%20clean%20design%20artistic%20background&width=1600&height=900&seq=hero-bg-manga&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                好看韩漫，尽在基德漫画
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                海量韩漫免费在线观看，独家下拉式阅读体验，高清画质、每日更新，让每一页都精彩纷呈
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/all"
                  className="px-8 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200"
                >
                  开始阅读
                </Link>
                <Link
                  to="/category/romance"
                  className="px-8 py-3 rounded-md bg-white/15 hover:bg-white/25 text-white font-medium text-sm whitespace-nowrap cursor-pointer backdrop-blur-sm transition-all duration-200 border border-white/20"
                >
                  热门推荐
                </Link>
              </div>
              <div className="flex items-center justify-center gap-6 mt-10">
                {[
                  { num: '5,000+', label: '漫画作品' },
                  { num: '120万+', label: '注册用户' },
                  { num: '98.5%', label: '好评率' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-2xl md:text-3xl font-bold text-white">{stat.num}</p>
                    <p className="text-xs text-white/60 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-2">本周最受欢迎的韩漫作品</p>
              </div>
              <Link to="/category/all" className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer transition-colors duration-200 font-medium">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {featuredComics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  cover={comic.cover}
                  rating={comic.rating}
                  status={comic.status}
                />
              ))}
            </div>
            <Link to="/category/all" className="sm:hidden flex items-center justify-center gap-1 mt-6 text-sm text-primary-600 font-medium cursor-pointer">
              查看全部漫画 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  分类浏览
                </h2>
                <p className="text-sm text-foreground-500 mt-2">按类型找到你喜欢的漫画</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {categories.filter(c => c.slug !== 'all').map((cat, idx) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-center gap-3 p-4 rounded-lg bg-background-50 hover:bg-primary-50 cursor-pointer transition-colors duration-200 border border-secondary-200/60 hover:border-primary-200"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-colors duration-200">
                    <i className={`text-lg ${['ri-heart-line','ri-magic-line','ri-fire-line','ri-emotion-line','ri-emotion-happy-line','ri-ghost-line','ri-hearts-line'][idx]}`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{cat.name}</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{cat.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-2">最近更新的韩漫章节</p>
              </div>
              <Link to="/category/all" className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer transition-colors duration-200 font-medium">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {latestComics.slice(0, 6).map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  cover={comic.cover}
                  rating={comic.rating}
                  status={comic.status}
                />
              ))}
            </div>
            <Link to="/category/all" className="sm:hidden flex items-center justify-center gap-1 mt-6 text-sm text-primary-600 font-medium cursor-pointer">
              查看全部更新 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  人气排行
                </h2>
                <p className="text-sm text-foreground-500 mt-2">阅读量最高的韩漫作品</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
              {popularComics.slice(0, 6).map((comic, idx) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group flex items-center gap-4 p-4 rounded-lg bg-background-50 hover:bg-primary-50/50 cursor-pointer transition-colors duration-200 border border-secondary-200/60"
                >
                  <span className={`font-heading text-2xl font-bold w-8 text-center flex-shrink-0 ${
                    idx < 3 ? 'text-primary-500' : 'text-foreground-300'
                  }`}>
                    {idx + 1}
                  </span>
                  <div className="w-14 h-20 flex-shrink-0 rounded-md overflow-hidden">
                    <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">{comic.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="flex items-center gap-0.5 text-xs text-primary-500 font-medium">
                        <i className="ri-star-fill text-[10px]"></i>{comic.rating}
                      </span>
                      <span className="text-xs text-foreground-400">{comic.categories.map(c => categories.find(cat => cat.slug === c)?.name).join(' / ')}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-xs text-foreground-400 bg-secondary-100 px-2.5 py-1 rounded-md whitespace-nowrap">
                      {comic.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-primary-500">
          <div className="max-w-[1280px] mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
              开启你的韩漫阅读之旅
            </h2>
            <p className="text-base text-white/80 mb-8 max-w-lg mx-auto leading-relaxed">
              数千部优质韩漫等你来探索，免费、高清、流畅，尽在基德漫画网
            </p>
            <Link
              to="/category/all"
              className="inline-flex px-8 py-3 rounded-md bg-white hover:bg-white/90 text-primary-600 font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200"
            >
              立即浏览漫画库
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}